import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { MessageSquare, Send, Loader2, CheckCircle, XCircle, RefreshCw, Wifi, WifiOff, Settings, Clock, Users, ChevronRight } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TEMPLATES = [
  { key: "fee_reminder", label: "Fee Reminder", desc: "Notify students about pending fees" },
  { key: "attendance_alert", label: "Attendance Alert", desc: "Alert parents/students about low attendance" },
  { key: "exam_notice", label: "Exam Notice", desc: "Send exam schedule notifications" },
  { key: "result_published", label: "Results Published", desc: "Notify when results are out" },
  { key: "payment_receipt", label: "Payment Confirmation", desc: "Confirm successful payment" },
];

function StatusBadge({ status, phone }) {
  const configs = {
    connected: { color: "#22C55E", bg: "#F0FDF4", border: "#BBF7D0", icon: Wifi, label: `Connected${phone ? ` · ${phone}` : ""}` },
    qr_ready: { color: "#F59E0B", bg: "#FFFBEB", border: "#FDE68A", icon: RefreshCw, label: "Scan QR Code to Connect" },
    connecting: { color: "#0A7AFF", bg: "#EFF6FF", border: "#BFDBFE", icon: Loader2, label: "Connecting..." },
    disconnected: { color: "#FF3636", bg: "#FFF1F2", border: "#FECDD3", icon: WifiOff, label: "Disconnected" },
  };
  const cfg = configs[status] || configs.disconnected;
  const Icon = cfg.icon;
  return (
    <div className="flex items-center gap-2 px-4 py-2 border" style={{ background: cfg.bg, borderColor: cfg.border }}>
      <Icon className={`w-4 h-4 ${status === "connecting" ? "animate-spin" : ""}`} style={{ color: cfg.color }} />
      <span className="font-['Syne'] font-bold text-[10px] uppercase tracking-widest" style={{ color: cfg.color }}>{cfg.label}</span>
    </div>
  );
}

export default function WhatsAppCenter() {
  const { user } = useAuth();
  const [tab, setTab] = useState("baileys");
  const [baileysStatus, setBaileysStatus] = useState({ status: "disconnected", connected: false });
  const [qrData, setQrData] = useState(null);
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState({ configured: false, phone_number_id: "", business_account_id: "" });
  const [configForm, setConfigForm] = useState({ phone_number_id: "", access_token: "", business_account_id: "" });
  const [sendForm, setSendForm] = useState({ to: "", message: "", provider: "baileys" });
  const [bulkNumbers, setBulkNumbers] = useState("");
  const [sending, setSending] = useState(false);
  const [savedConfig, setSavingConfig] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/whatsapp/baileys/status`, { withCredentials: true });
      setBaileysStatus(data);
    } catch {}
  }, []);

  const fetchQR = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/whatsapp/baileys/qr`, { withCredentials: true });
      setQrData(data);
      if (data.connected) {
        setBaileysStatus(prev => ({ ...prev, status: "connected", connected: true }));
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchQR();
    fetchHistory();
    fetchTemplates();
    fetchConfig();
  }, []);

  // Poll status and QR when not connected
  useEffect(() => {
    const interval = setInterval(() => {
      fetchStatus();
      if (!baileysStatus.connected) fetchQR();
    }, 5000);
    return () => clearInterval(interval);
  }, [baileysStatus.connected, fetchStatus, fetchQR]);

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/whatsapp/history`, { withCredentials: true });
      setHistory(data);
    } catch {}
  };

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/whatsapp/templates`, { withCredentials: true });
      setTemplates(data);
    } catch {}
  };

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/whatsapp/config`, { withCredentials: true });
      setConfig(data);
    } catch {}
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault(); setSavingConfig(true);
    try {
      await axios.post(`${API}/whatsapp/config`, configForm, { withCredentials: true });
      fetchConfig();
      setSendResult({ success: true, msg: "Configuration saved!" });
    } catch (err) {
      setSendResult({ success: false, msg: err.response?.data?.detail || "Save failed" });
    } finally { setSavingConfig(false); }
  };

  const handleSend = async (e) => {
    e.preventDefault(); setSending(true); setSendResult(null);
    try {
      await axios.post(`${API}/whatsapp/send`, {
        to: sendForm.to, message: sendForm.message, provider: tab === "official" ? "official" : "baileys"
      }, { withCredentials: true });
      setSendResult({ success: true, msg: `Message sent to ${sendForm.to}` });
      setSendForm(p => ({ ...p, to: "", message: "" }));
      fetchHistory();
    } catch (err) {
      setSendResult({ success: false, msg: err.response?.data?.detail || "Send failed" });
    } finally { setSending(false); }
  };

  const handleBulkSend = async () => {
    if (!sendForm.message || !bulkNumbers) return;
    setSending(true); setSendResult(null);
    const numbers = bulkNumbers.split(/[\n,]/).map(n => n.trim()).filter(Boolean);
    try {
      const { data } = await axios.post(`${API}/whatsapp/bulk-send`, {
        numbers, message: sendForm.message,
        provider: tab === "official" ? "official" : "baileys"
      }, { withCredentials: true });
      const sent = data.sent || data.results?.filter(r => r.success).length || 0;
      setSendResult({ success: true, msg: `Bulk sent to ${sent}/${numbers.length} recipients` });
      fetchHistory();
    } catch (err) {
      setSendResult({ success: false, msg: err.response?.data?.detail || "Bulk send failed" });
    } finally { setSending(false); }
  };

  const handleDisconnect = async () => {
    await axios.post(`${API}/whatsapp/baileys/disconnect`, {}, { withCredentials: true });
    setBaileysStatus({ status: "disconnected", connected: false });
    setQrData(null);
    setTimeout(fetchQR, 2000);
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-center">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Communications</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">WhatsApp Center</h2>
        </div>
        <StatusBadge status={baileysStatus.status} phone={baileysStatus.phone} />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Messages Sent", value: history.length, color: "#22C55E" },
          { label: "Via Personal", value: history.filter(h => h.provider === "baileys").length, color: "#8B5CF6" },
          { label: "Via Business", value: history.filter(h => h.provider === "official").length, color: "#0A7AFF" },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-[#E2DDD4] p-5" style={{ borderLeft: `3px solid ${s.color}` }}>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-2">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-3xl text-[#0C0C10]">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Provider Tabs */}
      <div className="flex gap-2 border-b border-[#D8D4CB]">
        {[
          ["baileys", "Personal WhatsApp", "Scan QR code to connect"],
          ["official", "Business WhatsApp", "Official Meta Cloud API"],
          ["history", "Message History", "All sent messages"],
        ].map(([t, label, sub]) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`wa-tab-${t}`}
            className={`px-5 py-3 text-left border-b-2 transition-all ${tab === t ? "border-[#0C0C10] text-[#0C0C10]" : "border-transparent text-[#BDBAA8]"}`}>
            <p className="font-['Syne'] font-bold text-[10px] uppercase tracking-widest">{label}</p>
            <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8] hidden sm:block">{sub}</p>
          </button>
        ))}
      </div>

      {/* Baileys Tab */}
      {tab === "baileys" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* QR / Status */}
          <div className="bg-white border border-[#E2DDD4] p-6">
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Connection Status</p>

            {baileysStatus.connected ? (
              <div className="text-center py-8 space-y-4">
                <div className="w-16 h-16 bg-[#22C55E]/10 border border-[#22C55E]/20 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-[#22C55E]" />
                </div>
                <p className="font-['Syne'] font-bold text-sm text-[#0C0C10]">WhatsApp Connected</p>
                {baileysStatus.phone && (
                  <p className="font-['JetBrains_Mono'] text-xs text-[#7A7468]">+{baileysStatus.phone}</p>
                )}
                <button onClick={handleDisconnect} data-testid="wa-disconnect-btn"
                  className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider border border-[#FF3636]/30 text-[#FF3636] px-5 py-2.5 hover:bg-[#FF3636]/8 transition-colors">
                  Disconnect
                </button>
              </div>
            ) : (
              <div className="text-center space-y-4">
                {qrData?.qr ? (
                  <>
                    <p className="font-['DM_Sans'] text-sm text-[#7A7468]">Scan with WhatsApp on your phone</p>
                    <img src={qrData.qr} alt="WhatsApp QR Code" className="mx-auto w-48 h-48 border border-[#E2DDD4]" />
                    <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#BDBAA8]">QR expires in ~60 seconds</p>
                  </>
                ) : (
                  <div className="py-10 space-y-4">
                    <div className="w-16 h-16 bg-[#0A7AFF]/10 border border-[#0A7AFF]/20 flex items-center justify-center mx-auto">
                      <Loader2 className="w-8 h-8 text-[#0A7AFF] animate-spin" />
                    </div>
                    <p className="font-['DM_Sans'] text-sm text-[#7A7468]">
                      {baileysStatus.status === "connecting" ? "Generating QR code..." : "Disconnected from WhatsApp"}
                    </p>
                    <button onClick={async () => {
                      await axios.post(`${API}/whatsapp/baileys/reconnect`, {}, { withCredentials: true });
                      setBaileysStatus(p => ({ ...p, status: "connecting" }));
                      setTimeout(fetchQR, 2000);
                    }} className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider bg-[#0C0C10] text-[#EAFF00] px-5 py-2.5 hover:bg-[#1C1C22] transition-colors flex items-center gap-2 mx-auto">
                      <RefreshCw className="w-3.5 h-3.5" /> Reconnect
                    </button>
                  </div>
                )}
                <div className="mt-4 p-3 bg-[#FFFBEB] border border-[#FDE68A]">
                  <p className="font-['DM_Sans'] text-xs text-[#D97706]">
                    <strong>How to connect:</strong> Open WhatsApp → Linked Devices → Link a Device → Scan QR
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Send Message */}
          <div className="bg-white border border-[#E2DDD4] p-6">
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Send Message</p>
            {!baileysStatus.connected && (
              <div className="mb-4 p-3 bg-[#FFF1F2] border border-[#FECDD3]">
                <p className="font-['DM_Sans'] text-xs text-[#DC2626]">Connect WhatsApp first by scanning the QR code.</p>
              </div>
            )}
            <form onSubmit={handleSend} className="space-y-4">
              <div>
                <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Phone Number</label>
                <input value={sendForm.to} onChange={e => setSendForm(p => ({ ...p, to: e.target.value }))} required
                  placeholder="919876543210 (with country code, no +)"
                  className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
              </div>
              <div>
                <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Message</label>
                <textarea value={sendForm.message} onChange={e => setSendForm(p => ({ ...p, message: e.target.value }))} required rows={4}
                  placeholder="Type your message..."
                  className="w-full bg-transparent border border-[#D8D4CB] p-3 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] resize-none transition-colors" />
              </div>

              {/* Quick templates */}
              <div>
                <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Quick Templates</label>
                <div className="flex flex-wrap gap-1.5">
                  {TEMPLATES.map(t => (
                    <button key={t.key} type="button"
                      onClick={() => setSendForm(p => ({...p, message: t.key + ": " + t.desc}))}
                      className="font-['Syne'] text-[9px] font-bold uppercase tracking-wider border border-[#D8D4CB] px-2.5 py-1.5 text-[#7A7468] hover:bg-[#0C0C10] hover:text-[#EAFF00] hover:border-[#0C0C10] transition-all">
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {sendResult && (
                <div className={`p-3 border ${sendResult.success ? "bg-[#F0FDF4] border-[#BBF7D0] text-[#15803D]" : "bg-[#FFF1F2] border-[#FECDD3] text-[#DC2626]"}`}>
                  <p className="font-['DM_Sans'] text-xs">{sendResult.msg}</p>
                </div>
              )}

              <button type="submit" disabled={sending} data-testid="wa-send-btn"
                className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Message</>}
              </button>
            </form>

            {/* Bulk send */}
            <div className="mt-5 pt-5 border-t border-[#F0ECE4]">
              <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-3">Bulk Send</p>
              <textarea value={bulkNumbers} onChange={e => setBulkNumbers(e.target.value)} rows={3}
                placeholder="Enter numbers separated by commas or new lines&#10;919876543210&#10;918765432109"
                className="w-full bg-transparent border border-[#D8D4CB] p-3 text-xs font-['JetBrains_Mono'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] resize-none transition-colors mb-3" />
              <button onClick={handleBulkSend} disabled={sending || !sendForm.message || !bulkNumbers} data-testid="wa-bulk-send-btn"
                className="w-full border border-[#D8D4CB] text-[#0C0C10] font-['Syne'] font-bold text-[9px] uppercase tracking-widest py-2.5 hover:bg-[#0C0C10] hover:text-[#EAFF00] hover:border-[#0C0C10] transition-all flex items-center justify-center gap-2 disabled:opacity-40">
                <Users className="w-3.5 h-3.5" /> Send to All Numbers
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Official API Tab */}
      {tab === "official" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white border border-[#E2DDD4] p-6">
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Business WhatsApp Configuration</p>
            {config.configured && (
              <div className="mb-4 p-3 bg-[#F0FDF4] border border-[#BBF7D0]">
                <p className="font-['DM_Sans'] text-xs text-[#15803D]">API configured. Last updated: {config.updated_at?.slice(0,10)}</p>
              </div>
            )}
            <div className="mb-5 p-4 bg-[#EFF6FF] border border-[#BFDBFE]">
              <p className="font-['Syne'] font-bold text-[10px] uppercase tracking-wider text-[#1D4ED8] mb-2">How to get credentials:</p>
              <ol className="font-['DM_Sans'] text-xs text-[#1D4ED8] space-y-1">
                <li>1. Go to <strong>developers.facebook.com</strong></li>
                <li>2. Create a Business app → Add WhatsApp product</li>
                <li>3. Get Phone Number ID and Access Token from dashboard</li>
                <li>4. Verify your business phone number</li>
              </ol>
            </div>
            <form onSubmit={handleSaveConfig} className="space-y-4">
              {[
                ["phone_number_id", "Phone Number ID"],
                ["access_token", "Access Token (Permanent)"],
                ["business_account_id", "Business Account ID (optional)"],
              ].map(([f, l]) => (
                <div key={f}>
                  <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-1.5">{l}</label>
                  <input type={f === "access_token" ? "password" : "text"} value={configForm[f]} onChange={e => setConfigForm(p => ({...p, [f]: e.target.value}))}
                    placeholder={f === "access_token" ? "EAAxxxxx..." : f === "phone_number_id" ? "123456789" : "Optional"}
                    className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['JetBrains_Mono'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
                </div>
              ))}
              <button type="submit" disabled={savedConfig} data-testid="save-wa-config-btn"
                className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {savedConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Settings className="w-4 h-4" /> Save Configuration</>}
              </button>
            </form>
          </div>

          <div className="bg-white border border-[#E2DDD4] p-6">
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Send via Business WhatsApp</p>
            {!config.configured && <div className="mb-4 p-3 bg-[#FFF1F2] border border-[#FECDD3]"><p className="font-['DM_Sans'] text-xs text-[#DC2626]">Configure API credentials first.</p></div>}
            <form onSubmit={handleSend} className="space-y-4">
              <div>
                <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Recipient Number (E.164)</label>
                <input value={sendForm.to} onChange={e => setSendForm(p => ({...p, to: e.target.value}))} required
                  placeholder="919876543210"
                  className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['JetBrains_Mono'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
              </div>
              <div>
                <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Message</label>
                <textarea value={sendForm.message} onChange={e => setSendForm(p => ({...p, message: e.target.value}))} required rows={5}
                  className="w-full bg-transparent border border-[#D8D4CB] p-3 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] resize-none transition-colors" />
              </div>
              {sendResult && (
                <div className={`p-3 border ${sendResult.success ? "bg-[#F0FDF4] border-[#BBF7D0] text-[#15803D]" : "bg-[#FFF1F2] border-[#FECDD3] text-[#DC2626]"}`}>
                  <p className="font-['DM_Sans'] text-xs">{sendResult.msg}</p>
                </div>
              )}
              <input type="hidden" value="official" />
              <button type="button" onClick={() => { setSendForm(p => ({...p})); handleSend({ preventDefault: () => {} }); }} disabled={sending || !config.configured} data-testid="wa-official-send-btn"
                className="w-full bg-[#0A7AFF] text-white font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#0060CC] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send via Business WhatsApp</>}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* History Tab */}
      {tab === "history" && (
        <div className="bg-white border border-[#E2DDD4]">
          <div className="px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7] grid" style={{ gridTemplateColumns: "120px 1fr 80px 80px 100px" }}>
            {["To", "Message", "Provider", "Status", "Sent At"].map(h => (
              <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
            ))}
          </div>
          {history.length === 0 ? (
            <div className="py-12 text-center">
              <MessageSquare className="w-8 h-8 text-[#D8D4CB] mx-auto mb-2" strokeWidth={1} />
              <p className="font-['DM_Sans'] text-sm text-[#BDBAA8]">No messages sent yet</p>
            </div>
          ) : history.map((h, i) => (
            <div key={i} className="grid items-center px-5 py-3 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7]"
              style={{ gridTemplateColumns: "120px 1fr 80px 80px 100px" }}>
              <span className="font-['JetBrains_Mono'] text-xs text-[#0C0C10]">{h.to}</span>
              <span className="font-['DM_Sans'] text-xs text-[#7A7468] truncate pr-4">{h.message}</span>
              <span className="font-['Syne'] font-bold text-[9px] uppercase tracking-wide px-2 py-1 border w-fit"
                style={h.provider === "baileys" ? { color: "#8B5CF6", borderColor: "#DDD6FE", background: "#F5F3FF" } : { color: "#0A7AFF", borderColor: "#BFDBFE", background: "#EFF6FF" }}>
                {h.provider === "baileys" ? "Personal" : "Business"}
              </span>
              <span className="font-['Syne'] font-bold text-[9px] uppercase tracking-wide px-2 py-1 border w-fit"
                style={h.success !== false ? { color: "#16A34A", borderColor: "#BBF7D0", background: "#F0FDF4" } : { color: "#DC2626", borderColor: "#FECDD3", background: "#FFF1F2" }}>
                {h.success !== false ? "Sent" : "Failed"}
              </span>
              <span className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{new Date(h.sent_at).toLocaleDateString("en-IN")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
